/**
 * Aba "Chuva" da barra lateral.
 *
 * O painel é deliberadamente curto: liga a camada, mostra a legenda e o
 * estado dos dados. Aparência fica atrás da engrenagem, igual à aba Mapa.
 */
import { CircleQuestionMark, CloudRain, ExternalLink, RefreshCw, Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatRelative } from '../lib/time';
import { avisoDeFrescor } from './frescor';
import { useTimeStore } from '../state/time';
import { Badge, Button, Dialog, Hint, IconButton, Section, Slider, Toggle } from '../ui/primitives';
import { GOTA_SVG } from './drop';
import { COR_SECA, COR_SEM_DADO, contarEstados } from './look';
import { FAIXAS, chuvaNaFatia, slotDe, totalDoPeriodo } from './series';
import { LIMITES, useStationStore } from './store';
import { carregarEstacoes } from './sync';

/** Gota em miniatura, na mesma forma do mapa. */
function Gota(props: { cor?: string; contorno: string; tracejado?: boolean; size?: number }) {
  const s = props.size ?? 13;
  return (
    <svg viewBox="-2.2 -2.2 4.4 4.4" width={s} height={s} aria-hidden className="shrink-0">
      <path
        d={GOTA_SVG}
        fill={props.cor ?? 'none'}
        stroke={props.contorno}
        strokeWidth={0.28}
        strokeDasharray={props.tracejado ? '0.42 0.42' : undefined}
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Legenda() {
  return (
    <div className="space-y-2">
      <div>
        <div className="flex gap-0.5">
          {FAIXAS.slice(1).map((f, i) => (
            <div key={i} className="flex-1" title={`${f.rotulo} — a partir de ${f.min} mm`}>
              <div className="h-2.5 rounded-[2px]" style={{ background: f.cor }} />
              <div className="mt-0.5 text-center text-[9px] text-ink-400 tabular-nums">{f.min}</div>
            </div>
          ))}
        </div>
        <Hint>Milímetros acumulados. A gota cresce com a chuva.</Hint>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-300">
        <span className="inline-flex items-center gap-1">
          <Gota cor={FAIXAS[3]!.cor} contorno="rgba(255,255,255,0.75)" /> choveu
        </span>
        <span className="inline-flex items-center gap-1">
          <Gota contorno={COR_SECA} /> mediu 0 mm
        </span>
        <span className="inline-flex items-center gap-1">
          <Gota contorno={COR_SEM_DADO} tracejado /> não reportou
        </span>
        <span className="inline-flex items-center gap-1">
          <svg viewBox="-2.2 -2.2 4.4 4.4" width={13} height={13} aria-hidden className="shrink-0">
            <circle cx="0" cy="0" r="1.75" fill="none" stroke="rgba(127,227,255,0.9)" strokeWidth="0.3" />
            <path d={GOTA_SVG} fill={FAIXAS[2]!.cor} transform="scale(0.55)" />
          </svg>
          chovendo agora
        </span>
      </div>
    </div>
  );
}

function AparenciaDialog(props: { open: boolean; onClose: () => void }) {
  const estilo = useStationStore((s) => s.estilo);
  const setEstilo = useStationStore((s) => s.setEstilo);
  return (
    <Dialog title="Aparência das estações" open={props.open} onClose={props.onClose}>
      <div className="space-y-4 p-4">
        <Slider label="Tamanho" value={estilo.tamanho} {...LIMITES.tamanho} onChange={(tamanho) => setEstilo({ tamanho })} />
        <Slider
          label="Opacidade"
          value={estilo.opacidade}
          {...LIMITES.opacidade}
          onChange={(opacidade) => setEstilo({ opacidade })}
        />
        <Toggle
          label="Mostrar estações que não reportaram"
          checked={estilo.mostrarSemDado}
          onChange={(mostrarSemDado) => setEstilo({ mostrarSemDado })}
          description="Gota tracejada onde o sensor ficou calado no instante do quadro."
        />
      </div>
    </Dialog>
  );
}

export function StationPanel() {
  const visivel = useStationStore((s) => s.visivel);
  const situacao = useStationStore((s) => s.situacao);
  const dados = useStationStore((s) => s.dados);
  const setVisivel = useStationStore((s) => s.setVisivel);
  const [aparenciaAberta, setAparenciaAberta] = useState(false);

  const aviso = avisoDeFrescor(dados.gerado);

  const displayedTime = useTimeStore((s) => s.displayedTime);
  const currentTime = useTimeStore((s) => s.currentTime);
  const instante = displayedTime ?? currentTime;

  // As contagens percorrem as ESTAÇÕES DESENHADAS, não as linhas da grade.
  // Os dois conjuntos divergem — estação no cadastro sem linha na grade, ou
  // linha sem cadastro —, e percorrer a grade fazia os números do painel não
  // fecharem com o que está no mapa.
  const totais = useMemo(() => {
    const g = dados.grade;
    return dados.estacoes.map((e) => {
      const i = g ? dados.linhaDe.get(e.cod) : undefined;
      return g && i !== undefined
        ? totalDoPeriodo(g.v[i], g.grandeza)
        : { mm: null, medidas: 0, fatias: 0 };
    });
  }, [dados.grade, dados.estacoes, dados.linhaDe]);

  const contagem = useMemo(() => {
    const g = dados.grade;
    if (!g) return null;
    const slot = instante === null ? -1 : slotDe(g, instante);
    const agora = dados.estacoes.map((e) => {
      const i = dados.linhaDe.get(e.cod);
      return slot < 0 || i === undefined ? null : chuvaNaFatia(g.v[i], slot, g.grandeza);
    });
    return contarEstados(totais, agora);
  }, [dados.grade, dados.estacoes, dados.linhaDe, totais, instante]);

  return (
    <div>
      <Section
        title="Chuva medida"
        icon={CloudRain}
        right={
          visivel && (
            <IconButton
              icon={Settings2}
              size="sm"
              label="Aparência das estações"
              onClick={() => setAparenciaAberta(true)}
              className="pointer-coarse:size-11"
            />
          )
        }
      >
        <Toggle
          label="Pluviômetros do CEMADEN"
          checked={visivel}
          onChange={setVisivel}
          description="Estações automáticas da Amazônia Legal, sincronizadas com o quadro exibido."
        />

        {visivel && aviso && (
          <div className="mt-2 rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}

        {visivel && (
          <>
            {contagem && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2.5">
                <Badge tone="rio">{formatInt(contagem.chuva)} choveram em 24 h</Badge>
                {contagem.agora > 0 && <Badge tone="mata">{formatInt(contagem.agora)} chovendo agora</Badge>}
                <Badge>{formatInt(contagem.seca)} sem chuva</Badge>
                {contagem.semDado > 0 && <Badge>{formatInt(contagem.semDado)} sem dado</Badge>}
              </div>
            )}
            <Hint>
              A cor da gota é a chuva das últimas 24 h — o período que o CEMADEN publica — e não muda com o quadro. O
              anel só acende nas estações que estão recebendo chuva no instante exibido.
            </Hint>
            <div className="pt-3">
              <Legenda />
            </div>
          </>
        )}
      </Section>

      <Section title="Dados" icon={RefreshCw} defaultOpen={false}>
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao baixar: {situacao.erro}</div>}
          {situacao.mensagem && <div className="text-alerta-300">{situacao.mensagem}</div>}
          {situacao.estado === 'ready' && (
            <div>
              {formatInt(dados.estacoes.length)} estações
              {dados.gerado !== null && <> · atualizado {formatRelative(dados.gerado)}</>}
              {dados.modo === 'ped' && <> · API oficial</>}
              {dados.modo === 'aberto' && <> · fonte aberta</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarEstacoes(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false}>
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            A Rede Observacional do CEMADEN tem pluviômetros automáticos espalhados pelo país
            {dados.estacoes.length > 0 && <> — {formatInt(dados.estacoes.length)} deles na Amazônia Legal</>}. Cada
            gota no mapa é uma estação, colorida pela chuva acumulada até o instante do quadro exibido.
          </p>
          <p>
            A gota fica parada: a cor conta quanto choveu nas últimas 24 h. Enquanto a animação corre, o anel azul
            acende só onde está chovendo naquele instante.
          </p>
          <p>Clique numa estação para ver as últimas 24 horas, hora a hora.</p>
          <p className="text-ink-400">
            Fonte: Dados da Rede Observacional do CEMADEN/MCTI.{' '}
            <a
              href="https://www.gov.br/cemaden/pt-br"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              gov.br/cemaden
              <ExternalLink size={10} />
            </a>
          </p>
        </div>
      </Section>

      <AparenciaDialog open={aparenciaAberta} onClose={() => setAparenciaAberta(false)} />
    </div>
  );
}
